import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  Users, 
  Settings,
  BarChart3,
  X,
  Loader,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2
} from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import whatsappService from '../../services/whatsappService';

const WhatsAppMessaging = ({ coachingId, coachingName }) => {
  const [activeTab, setActiveTab] = useState('send');
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageType, setMessageType] = useState('custom');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('individual');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [customPhones, setCustomPhones] = useState(['']);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const [apiConfig, setApiConfig] = useState({
    apiKey: '',
    apiUrl: '',
    provider: 'custom'
  });
  const [showSettings, setShowSettings] = useState(false);
  
  // Dynamic data for message types
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [homeworkList, setHomeworkList] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState('');
  const [dynamicData, setDynamicData] = useState({});

  useEffect(() => {
    if (coachingId) {
      loadData();
      loadMonthlyStats();
      loadMessageHistory();
    } else {
      console.error('No coachingId provided to WhatsAppMessaging component');
      toast.error('Coaching ID not found. Please try again.');
    }
  }, [coachingId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('Loading WhatsApp data for coachingId:', coachingId);
      
      if (!coachingId) {
        throw new Error('Coaching ID is required');
      }
      
      // Load students
      const studentsRef = collection(db, 'students');
      const studentsQuery = query(studentsRef, where('coachingId', '==', coachingId));
      const studentsSnapshot = await getDocs(studentsQuery);
      const studentsData = studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Loaded students:', studentsData.length);
      setStudents(studentsData);
      
      // Load batches
      const batchesRef = collection(db, 'batches');
      const batchesQuery = query(batchesRef, where('coachingId', '==', coachingId));
      const batchesSnapshot = await getDocs(batchesQuery);
      const batchesData = batchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Loaded batches:', batchesData.length);
      setBatches(batchesData);
      
      // Load tests
      const testsRef = collection(db, 'tests');
      const testsQuery = query(testsRef, where('coachingId', '==', coachingId));
      const testsSnapshot = await getDocs(testsQuery);
      const testsData = testsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Loaded tests:', testsData.length);
      setTests(testsData);
      
      // Load classes/schedules
      const classesRef = collection(db, 'batchClasses');
      const classesQuery = query(classesRef, where('coachingId', '==', coachingId));
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Loaded classes:', classesData.length);
      setClasses(classesData);
      
      // Load homework
      const homeworkRef = collection(db, 'homework');
      const homeworkQuery = query(homeworkRef, where('coachingId', '==', coachingId));
      const homeworkSnapshot = await getDocs(homeworkQuery);
      const homeworkData = homeworkSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Loaded homework:', homeworkData.length);
      setHomeworkList(homeworkData);
      
      // Initialize WhatsApp service
      await whatsappService.initialize(coachingId);
      
      console.log('WhatsApp data loaded successfully');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyStats = async () => {
    const now = new Date();
    const stats = await whatsappService.getMonthlyStats(coachingId, now.getFullYear(), now.getMonth() + 1);
    setMonthlyStats(stats);
  };

  const loadMessageHistory = async () => {
    const history = await whatsappService.getMessageHistory(coachingId);
    setMessageHistory(history);
  };

  const handleSaveSettings = async () => {
    try {
      const result = await whatsappService.saveConfiguration(coachingId, apiConfig);
      if (result.success) {
        toast.success('WhatsApp settings saved successfully');
        setShowSettings(false);
      } else {
        toast.error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handlePreviewRecipients = async () => {
    try {
      setLoading(true);
      const filters = buildFilters();
      console.log('=== PREVIEW RECIPIENTS DEBUG ===');
      console.log('Filters:', JSON.stringify(filters, null, 2));
      console.log('Recipient Type:', recipientType);
      console.log('Selected Batch IDs:', selectedBatchIds);
      console.log('Coaching ID:', coachingId);
      console.log('Available Students:', students.length);
      console.log('Available Batches:', batches.length);
      
      const recipients = await whatsappService.getRecipients(coachingId, filters);
      console.log('Found recipients:', recipients.length);
      console.log('Recipients:', JSON.stringify(recipients, null, 2));
      
      if (recipients.length === 0) {
        toast.error('No recipients found. Make sure students have phone numbers and are enrolled in the selected batch(es).');
      }
      
      setSelectedRecipients(recipients);
      setShowPreview(true);
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load recipients: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = () => {
    const filters = {};
    
    if (recipientType === 'individual' && selectedStudentIds.length === 1) {
      filters.studentId = selectedStudentIds[0];
    } else if (recipientType === 'multiple' && selectedStudentIds.length > 0) {
      filters.studentIds = selectedStudentIds;
    } else if (recipientType === 'batch' && selectedBatchIds.length === 1) {
      filters.batchId = selectedBatchIds[0];
    } else if (recipientType === 'multipleBatches' && selectedBatchIds.length > 0) {
      // If only one batch selected, use batchId instead of batchIds for consistency
      if (selectedBatchIds.length === 1) {
        filters.batchId = selectedBatchIds[0];
      } else {
        filters.batchIds = selectedBatchIds;
      }
    } else if (recipientType === 'allStudents') {
      filters.allStudents = true;
    } else if (recipientType === 'custom') {
      filters.customPhones = customPhones.filter(p => p.trim());
    }
    
    console.log('Built filters:', filters);
    return filters;
  };

  const handleSendMessages = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('No recipients selected');
      return;
    }

    // Validate based on message type
    if (messageType === 'testResult' && !selectedTest) {
      toast.error('Please select a test');
      return;
    }
    
    if (messageType !== 'testResult' && !message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);
      
      // Prepare personalized messages for each recipient
      const personalizedMessages = await Promise.all(
        selectedRecipients.map(async (recipient) => {
          let personalizedMessage = message;
          
          // Only auto-generate for test results
          if (messageType === 'testResult' && selectedTest) {
            personalizedMessage = await generateTestResultMessage(recipient, selectedTest);
          } else {
            // For other types, just replace student name placeholder
            personalizedMessage = personalizedMessage
              .replace(/{studentName}/g, recipient.name)
              .replace(/{coachingName}/g, coachingName);
          }
          
          return {
            ...recipient,
            personalizedMessage
          };
        })
      );

      const messageData = {
        recipients: personalizedMessages,
        message: message.trim() || 'Auto-generated message',
        messageType,
        metadata: {
          recipientType,
          coachingName,
          sentBy: 'admin',
          testId: selectedTest || null,
          classId: selectedClass || null,
          homeworkId: selectedHomework || null
        }
      };

      const results = await whatsappService.sendBulkMessages(coachingId, messageData);
      
      if (results.successful > 0) {
        toast.success(`Successfully sent ${results.successful} message(s)`);
      }
      
      if (results.failed > 0) {
        toast.error(`Failed to send ${results.failed} message(s)`);
      }

      // Reset form
      setMessage('');
      setSelectedRecipients([]);
      setShowPreview(false);
      setSelectedStudentIds([]);
      setSelectedBatchIds([]);
      setCustomPhones(['']);
      setSelectedTest('');
      setSelectedClass('');
      setSelectedHomework('');
      
      // Reload stats and history
      loadMonthlyStats();
      loadMessageHistory();
    } catch (error) {
      console.error('Error sending messages:', error);
      toast.error('Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  const generateTestResultMessage = async (recipient, testId) => {
    try {
      // Fetch test details
      const testDoc = await getDocs(query(collection(db, 'tests'), where('__name__', '==', testId)));
      if (testDoc.empty) return message;
      
      const testData = testDoc.docs[0].data();
      
      // Fetch student's test result
      const resultsRef = collection(db, 'testResults');
      const resultsQuery = query(
        resultsRef,
        where('testId', '==', testId),
        where('userId', '==', recipient.id)
      );
      const resultsSnapshot = await getDocs(resultsQuery);
      
      if (resultsSnapshot.empty) {
        return `Dear ${recipient.name},\n\nYour result for ${testData.title} is not available yet.\n\nRegards,\n${coachingName}`;
      }
      
      const result = resultsSnapshot.docs[0].data();
      const score = result.score || 0;
      const totalMarks = testData.totalMarks || 100;
      const percentage = ((score / totalMarks) * 100).toFixed(2);
      
      // Get rank (if available)
      const allResults = await getDocs(query(resultsRef, where('testId', '==', testId)));
      const sortedResults = allResults.docs
        .map(doc => doc.data())
        .sort((a, b) => (b.score || 0) - (a.score || 0));
      const rank = sortedResults.findIndex(r => r.userId === recipient.id) + 1;
      
      return `Dear ${recipient.name},\n\nYour test results for ${testData.title}:\n\nScore: ${score}/${totalMarks}\nPercentage: ${percentage}%\nRank: ${rank}${rank === 1 ? ' 🏆' : ''}\n\n${percentage >= 75 ? 'Excellent performance! Keep it up!' : percentage >= 50 ? 'Good effort! Keep working hard!' : 'Keep practicing, you can do better!'}\n\nRegards,\n${coachingName}`;
    } catch (error) {
      console.error('Error generating test result message:', error);
      return message;
    }
  };

  const generateClassMessage = async (recipient, classId) => {
    try {
      const classDoc = await getDocs(query(collection(db, 'batchClasses'), where('__name__', '==', classId)));
      if (classDoc.empty) return message;
      
      const classData = classDoc.docs[0].data();
      const classDate = classData.date ? new Date(classData.date.seconds * 1000).toLocaleDateString() : 'TBD';
      const classTime = classData.time || 'TBD';
      
      return `Dear ${recipient.name},\n\nClass scheduled for ${classData.subject || 'Subject'}:\n\nDate: ${classDate}\nTime: ${classTime}\nVenue: ${classData.venue || 'TBD'}\nTopic: ${classData.topic || 'TBD'}\n\nPlease be on time.\n\nRegards,\n${coachingName}`;
    } catch (error) {
      console.error('Error generating class message:', error);
      return message;
    }
  };

  const generateHomeworkMessage = async (recipient, homeworkId) => {
    try {
      const homeworkDoc = await getDocs(query(collection(db, 'homework'), where('__name__', '==', homeworkId)));
      if (homeworkDoc.empty) return message;
      
      const homeworkData = homeworkDoc.docs[0].data();
      const dueDate = homeworkData.dueDate ? new Date(homeworkData.dueDate.seconds * 1000).toLocaleDateString() : 'TBD';
      
      return `Dear ${recipient.name},\n\nHomework assigned for ${homeworkData.subject || 'Subject'}:\n\n${homeworkData.description || homeworkData.title}\n\nDue Date: ${dueDate}\n\nPlease complete on time.\n\nRegards,\n${coachingName}`;
    } catch (error) {
      console.error('Error generating homework message:', error);
      return message;
    }
  };

  const addCustomPhone = () => {
    setCustomPhones([...customPhones, '']);
  };

  const removeCustomPhone = (index) => {
    setCustomPhones(customPhones.filter((_, i) => i !== index));
  };

  const updateCustomPhone = (index, value) => {
    const updated = [...customPhones];
    updated[index] = value;
    setCustomPhones(updated);
  };

  const templates = whatsappService.getMessageTemplates();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <MessageSquare className="w-6 h-6 mr-2" />
              WhatsApp Messaging
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Send WhatsApp messages to students and track delivery
            </p>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mt-6">
          <button
            onClick={() => setActiveTab('send')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'send'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Send className="w-4 h-4 inline mr-2" />
            Send Messages
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'stats'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Statistics
          </button>
        </div>
      </div>

      {/* Send Messages Tab */}
      {activeTab === 'send' && (
        <div className="p-6 space-y-6">
          {/* Message Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message Type
            </label>
            <select
              value={messageType}
              onChange={(e) => {
                setMessageType(e.target.value);
                setSelectedTest('');
                setSelectedClass('');
                setSelectedHomework('');
                if (e.target.value !== 'custom' && e.target.value !== 'marketing' && e.target.value !== 'testResult') {
                  setMessage(templates[e.target.value].template);
                } else {
                  setMessage('');
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="class">Class Notification</option>
              <option value="announcement">Announcement</option>
              <option value="testResult">Test Result (Auto-fetch scores)</option>
              <option value="homework">Homework Reminder</option>
              <option value="custom">Custom Message</option>
              <option value="marketing">Marketing Message</option>
            </select>
          </div>

          {/* Dynamic Test Result Selector */}
          {messageType === 'testResult' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Test
              </label>
              <select
                value={selectedTest}
                onChange={(e) => setSelectedTest(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Choose a test...</option>
                {tests.map(test => (
                  <option key={test.id} value={test.id}>
                    {test.title} - {test.subject}
                  </option>
                ))}
              </select>
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                      🎯 Automatic Personalization
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Each student will receive their individual test score, percentage, and rank automatically. No need to type anything!
                    </p>
                    {selectedTest && (
                      <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs">
                        <p className="font-medium mb-1">Sample Message:</p>
                        <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
                          Dear [Student Name],{'\n\n'}
                          Your test results for [Test Name]:{'\n\n'}
                          Score: [Score]/[Total]{'\n'}
                          Percentage: [Percentage]%{'\n'}
                          Rank: [Rank]{'\n\n'}
                          Regards,{'\n'}
                          {coachingName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recipient Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Send To
            </label>
            <select
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setSelectedStudentIds([]);
                setSelectedBatchIds([]);
                setCustomPhones(['']);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="individual">Individual Student</option>
              <option value="multiple">Multiple Students</option>
              <option value="batch">Single Batch</option>
              <option value="multipleBatches">Multiple Batches</option>
              <option value="allStudents">All Students</option>
              <option value="custom">Custom Phone Numbers (Marketing)</option>
            </select>
          </div>

          {/* Individual Student Selection */}
          {recipientType === 'individual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Student
              </label>
              <select
                value={selectedStudentIds[0] || ''}
                onChange={(e) => setSelectedStudentIds([e.target.value])}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Choose a student...</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} - {student.phone}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiple Students Selection */}
          {recipientType === 'multiple' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Students
              </label>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-60 overflow-y-auto">
                {students.map(student => (
                  <label key={student.id} className="flex items-center space-x-2 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudentIds([...selectedStudentIds, student.id]);
                        } else {
                          setSelectedStudentIds(selectedStudentIds.filter(id => id !== student.id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-900 dark:text-white">{student.name} - {student.phone}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Single Batch Selection */}
          {recipientType === 'batch' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Batch
              </label>
              <select
                value={selectedBatchIds[0] || ''}
                onChange={(e) => setSelectedBatchIds([e.target.value])}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Choose a batch...</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name} - {batch.subject}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiple Batches Selection */}
          {recipientType === 'multipleBatches' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Batches
              </label>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-60 overflow-y-auto">
                {batches.map(batch => (
                  <label key={batch.id} className="flex items-center space-x-2 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedBatchIds.includes(batch.id)}
                      onChange={(e) => {
                        console.log('Batch checkbox changed:', batch.id, e.target.checked);
                        if (e.target.checked) {
                          const newIds = [...selectedBatchIds, batch.id];
                          console.log('New selected batch IDs:', newIds);
                          setSelectedBatchIds(newIds);
                        } else {
                          const newIds = selectedBatchIds.filter(id => id !== batch.id);
                          console.log('New selected batch IDs:', newIds);
                          setSelectedBatchIds(newIds);
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-900 dark:text-white">{batch.name} - {batch.subject}</span>
                  </label>
                ))}
              </div>
              {selectedBatchIds.length > 0 && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                  {selectedBatchIds.length} batch(es) selected
                </p>
              )}
            </div>
          )}

          {/* Custom Phone Numbers */}
          {recipientType === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Numbers
              </label>
              <div className="space-y-2">
                {customPhones.map((phone, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => updateCustomPhone(index, e.target.value)}
                      placeholder="+91 1234567890"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    {customPhones.length > 1 && (
                      <button
                        onClick={() => removeCustomPhone(index)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addCustomPhone}
                  className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Phone Number</span>
                </button>
              </div>
            </div>
          )}

          {/* Message Content */}
          {messageType !== 'testResult' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message {messageType === 'class' || messageType === 'homework' || messageType === 'announcement' ? '(You can customize the template)' : ''}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder={
                  messageType === 'class' ? 'Dear {studentName},\n\nClass scheduled for [Subject] on [Date] at [Time]...' :
                  messageType === 'homework' ? 'Dear {studentName},\n\nHomework assigned for [Subject]...' :
                  messageType === 'announcement' ? 'Dear {studentName},\n\n[Your announcement here]...' :
                  'Enter your message here...'
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Characters: {message.length}
                </p>
                {(messageType === 'class' || messageType === 'homework' || messageType === 'announcement') && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Use {'{studentName}'} for personalization
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handlePreviewRecipients}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Preview Recipients
            </button>
            <button
              onClick={handleSendMessages}
              disabled={sending || selectedRecipients.length === 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? (
                <>
                  <Loader className="w-4 h-4 inline mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 inline mr-2" />
                  Send Messages
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && (
        <div className="p-6 space-y-6">
          {/* Monthly Stats */}
          {monthlyStats && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                This Month's Statistics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Messages</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {monthlyStats.totalMessages || 0}
                  </p>
                </div>
                {monthlyStats.messageTypes && Object.entries(monthlyStats.messageTypes).map(([type, count]) => (
                  <div key={type} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{type}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message History */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recent Messages
            </h3>
            <div className="space-y-3">
              {messageHistory.slice(0, 10).map((msg) => (
                <div key={msg.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                          {msg.messageType}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          msg.status === 'completed' 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        }`}>
                          {msg.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {msg.message?.substring(0, 100)}...
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>Recipients: {msg.totalRecipients}</span>
                        <span>Sent: {msg.successfulCount || 0}</span>
                        <span>Failed: {msg.failedCount || 0}</span>
                        {msg.sentAt && (
                          <span>{new Date(msg.sentAt.seconds * 1000).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Preview Recipients Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Preview Recipients ({selectedRecipients.length})
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {selectedRecipients.map((recipient, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{recipient.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{recipient.phone}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => setShowPreview(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                WhatsApp API Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Provider
                </label>
                <select
                  value={apiConfig.provider}
                  onChange={(e) => setApiConfig({ ...apiConfig, provider: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="custom">Custom API</option>
                  <option value="twilio">Twilio</option>
                  <option value="whatsapp-business">WhatsApp Business API</option>
                  <option value="gupshup">Gupshup</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API URL
                </label>
                <input
                  type="text"
                  value={apiConfig.apiUrl}
                  onChange={(e) => setApiConfig({ ...apiConfig, apiUrl: e.target.value })}
                  placeholder="https://api.example.com/send"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={apiConfig.apiKey}
                  onChange={(e) => setApiConfig({ ...apiConfig, apiKey: e.target.value })}
                  placeholder="Enter your API key"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Make sure to configure your WhatsApp API provider correctly. The API key will be stored securely.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex space-x-3">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppMessaging;
